import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify, useRefresh, useVersion } from 'react-admin';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';
import { FileCopyOutlined } from '@material-ui/icons';
import classNames from 'classnames';
import clipboardCopy from 'clipboard-copy';

import { Confirmation } from 'components/common';
import stripeVerificationIcon from 'assets/icons/stripe-verification.svg';
import stripeExternalLinkIcon from 'assets/icons/stripe-external-link.svg';

import { UserDetails } from '../types';
import { UserWriteAccess } from 'contexts/adminAccess';

const DEFAULT_KYC_EXPIRY_HOURS = 24;
const KYC_EXPIRY_HOURS_OPTIONS = [6, 12, 24, 48];

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  label: {
    fontSize: 16,
    lineHeight: '20px',
    color: theme.palette.secondary.contrastText,
    fontWeight: 700,
  },
  button: {
    color: theme.palette.secondary.contrastText,
    textTransform: 'none',
    minWidth: 0,
    minHeight: 32,
    padding: '4px 16px',
    fontSize: 12,
    lineHeight: '20px',
    borderRadius: 26,
    borderColor: '#E1E1E1',
    whiteSpace: 'nowrap',
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    maxWidth: '100%',
  },
  sectionTitle: {
    fontSize: 16,
    lineHeight: '20px',
    color: theme.palette.secondary.contrastText,
    fontWeight: 700,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  statusDescription: {
    color: theme.palette.secondary.contrastText,
    fontSize: 11,
    lineHeight: '20px',
    marginTop: theme.spacing(0.5),
  },
  statusChip: {
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    lineHeight: '16px',
    fontWeight: 700,
  },
  statusChipSuccess: {
    backgroundColor: '#DFFFE6',
    color: '#0CA82D',
  },
  statusChipWarning: {
    backgroundColor: 'rgba(255, 194, 14, 0.1)',
    color: '#D78817',
  },
  statusChipError: {
    backgroundColor: '#FDE0E0',
    color: '#B00020',
  },
  statusChipNeutral: {
    backgroundColor: '#E8EBF0',
    color: theme.palette.text.primary,
  },
  helperText: {
    color: theme.palette.secondary.contrastText,
    fontSize: 12,
    lineHeight: '20px',
    marginTop: theme.spacing(0.5),
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: theme.spacing(1),
  },
  actionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  actionsSectionItem: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
  },
  actionDescription: {
    flex: '1 1 212px',
    minWidth: 0,
  },
  detailsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
  },
  detailsLabel: {
    flex: '0 1 250px',
  },
  detailsValue: {
    flex: '1 1 240px',
    minWidth: 0,
  },
  valueText: {
    fontSize: 14,
    lineHeight: '20px',
    overflowWrap: 'anywhere',
    minWidth: 0,
  },
  stripeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  stripeIcon: {
    width: 24,
    height: 24,
  },
  externalLinkIcon: {
    width: 18,
    height: 18,
    marginLeft: 2,
  },
  sessionValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  copyButton: {
    padding: 4,
    color: theme.palette.secondary.contrastText,
    flexShrink: 0,
    '& svg': {
      fontSize: 16,
    },
  },
  primaryButton: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  destructiveButton: {
    borderColor: '#FF0000',
    color: '#FF0000',
  },
  gracePeriodButtonSelected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const formatKycTimer = (seconds: number | null) => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) {
    return '--:--';
  }
  if (seconds === -1 || seconds === 0) {
    return '--:--';
  }

  // Assumes API returns seconds; display HH:MM by truncating seconds.
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

interface Props {
  user: UserDetails;
}

const IdentityVerification = ({ user }: Props) => {
  const { id: userId, kycStatus, kycSessionId, kycStripeLink } = user;

  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const version = useVersion();

  const [kycLoading, setKycLoading] = useState<boolean>(false);
  const [kycTimerSeconds, setKycTimerSeconds] = useState<number | null>(null);
  const [kycExpiryHours, setKycExpiryHours] = useState<number>(DEFAULT_KYC_EXPIRY_HOURS);

  useEffect(() => {
    if (!userId) {
      setKycTimerSeconds(null);
      return;
    }

    const fetchKycTimer = async () => {
      try {
        // Assumes API returns seconds and ignores the appended userId query param.
        const { data } = await dataProvider.get('userKycTimer', { userId });
        const nextValue = typeof data === 'number' ? data : Number(data);

        setKycTimerSeconds(Number.isFinite(nextValue) ? nextValue : null);
      } catch (error: any) {
        notify(
          typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
          'warning'
        );
        setKycTimerSeconds(null);
      }
    };

    fetchKycTimer();
  }, [dataProvider, notify, userId, version]);

  // Assumes user id matches {userId} for KYC endpoints and the API accepts expiryHours in the body.
  const handleStartKyc = async () => {
    if (!userId) {
      return;
    }

    try {
      setKycLoading(true);

      await dataProvider.post('userKyc', { userId, data: { expiryHours: kycExpiryHours } });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setKycLoading(false);
    }
  };

  const handleResetKycTimer = async () => {
    if (!userId) {
      return;
    }

    try {
      setKycLoading(true);

      await dataProvider.update('userKycTimer', { userId, data: { expiryHours: kycExpiryHours } });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setKycLoading(false);
    }
  };

  const handleDropKycStatus = async () => {
    if (!userId) {
      return;
    }

    try {
      setKycLoading(true);

      await dataProvider.delete('userKyc', { userId });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setKycLoading(false);
    }
  };

  const handleCompleteKycStatus = async () => {
    if (!userId) {
      return;
    }

    try {
      setKycLoading(true);

      await dataProvider.update('userKycComplete', { userId });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setKycLoading(false);
    }
  };

  const normalizedStatus = kycStatus ?? 'NOT_STARTED';
  const statusInfoMap: Record<
    string,
    { label: string; description: string; tone: 'success' | 'warning' | 'error' | 'neutral' }
  > = {
    NOT_STARTED: {
      label: 'Not started',
      description: 'No verification request has been initiated yet.',
      tone: 'neutral',
    },
    REQUIRED: {
      label: 'Required',
      description: 'Verification is required before access can continue.',
      tone: 'warning',
    },
    STARTED: {
      label: 'In progress',
      description: 'User has started the verification process.',
      tone: 'warning',
    },
    COMPLETED: {
      label: 'Completed',
      description: 'Verification has been completed.',
      tone: 'success',
    },
    FAILED: {
      label: 'Failed',
      description: 'Verification failed and needs review.',
      tone: 'error',
    },
  };
  const statusInfo = statusInfoMap[normalizedStatus] ?? statusInfoMap.NOT_STARTED;
  const statusChipClass = classNames(
    classes.statusChip,
    statusInfo.tone === 'success' && classes.statusChipSuccess,
    statusInfo.tone === 'warning' && classes.statusChipWarning,
    statusInfo.tone === 'error' && classes.statusChipError,
    statusInfo.tone === 'neutral' && classes.statusChipNeutral
  );

  const formattedKycTimer = formatKycTimer(kycTimerSeconds);
  const hasValidTimer = typeof kycTimerSeconds === 'number' && Number.isFinite(kycTimerSeconds);
  const isSelfVerification = normalizedStatus !== 'NOT_STARTED' && kycTimerSeconds === -1;
  const kycTypeLabel =
    normalizedStatus === 'NOT_STARTED'
      ? '--'
      : isSelfVerification
        ? 'Self verification'
        : 'Admin request';
  const kycTypeInfo = {
    label: kycTypeLabel,
    helper:
      kycTypeLabel === '--'
        ? 'Verification type is set after the process starts.'
        : isSelfVerification
          ? 'User completes verification without an admin timer.'
          : 'Admin initiated verification with a timer.',
  };
  const timerInfo = (() => {
    if (normalizedStatus === 'NOT_STARTED') {
      return {
        label: 'No active timer',
        helper: 'Timer starts after verification begins.',
      };
    }
    if (isSelfVerification) {
      return {
        label: 'No timer (self verification)',
        helper: 'Self verification does not include a countdown.',
      };
    }
    if (kycTimerSeconds === 0) {
      return {
        label: 'Expired',
        helper: 'Admin-requested verification window has ended.',
      };
    }
    if (!hasValidTimer) {
      return {
        label: 'Unavailable',
        helper: 'Timer could not be loaded.',
      };
    }
    return {
      label: formattedKycTimer,
      helper: 'Time remaining for the user to complete verification.',
    };
  })();

  const isResetTimerAvailable =
    !isSelfVerification && (normalizedStatus === 'FAILED' || normalizedStatus === 'REQUIRED');
  const isResetTimerDisabled = kycLoading || !isResetTimerAvailable;
  const isStartDisabled = kycLoading || normalizedStatus !== 'NOT_STARTED';
  const isDropDisabled =
    kycLoading || normalizedStatus === 'NOT_STARTED' || normalizedStatus === 'COMPLETED';
  const isCompleteDisabled =
    kycLoading || normalizedStatus === 'NOT_STARTED' || normalizedStatus === 'COMPLETED';
  const primaryActionKey = !isStartDisabled
    ? 'start'
    : !isCompleteDisabled
      ? 'complete'
      : !isDropDisabled
        ? 'drop'
        : null;
  const primaryActionHelper = primaryActionKey
    ? `Recommended: ${
        primaryActionKey === 'start'
          ? 'Start verification'
          : primaryActionKey === 'complete'
            ? 'Mark as completed'
            : 'Clear verification status'
      }.`
    : 'No primary action is available for the current status.';
  const resetTimerConfirmContent =
    `Reset the timer for this verification request with a ${kycExpiryHours} ` +
    'hour grace period?';

  const handleCopyKycId = async () => {
    if (!kycSessionId) {
      return;
    }

    try {
      await clipboardCopy(kycSessionId);
      notify('KYC ID is copied');
    } catch (error: any) {
      notify(typeof error === 'string' ? error : error.message, 'error');
    }
  };

  const renderGracePeriodSelector = () =>
    KYC_EXPIRY_HOURS_OPTIONS.map((hours) => {
      const isSelected = kycExpiryHours === hours;

      return (
        <Button
          key={hours}
          variant={isSelected ? 'contained' : 'outlined'}
          className={classNames(classes.button, isSelected && classes.gracePeriodButtonSelected)}
          onClick={() => setKycExpiryHours(hours)}
          disabled={kycLoading}>
          {hours} hrs
        </Button>
      );
    });

  const renderActionButton = (actionKey: 'start' | 'complete' | 'drop' | 'reset') => {
    const isPrimary = primaryActionKey === actionKey;
    const actionConfig =
      actionKey === 'start'
        ? {
            label: 'Start verification',
            onClick: handleStartKyc,
            disabled: isStartDisabled,
          }
        : actionKey === 'complete'
          ? {
              label: 'Mark as completed',
              onClick: handleCompleteKycStatus,
              disabled: isCompleteDisabled,
            }
          : actionKey === 'drop'
            ? {
                label: 'Clear verification status',
                onClick: handleDropKycStatus,
                disabled: isDropDisabled,
                confirmTitle: 'Clear verification status',
                confirmContent: 'Are you sure you want to clear the verification status?',
              }
            : {
                label: 'Reset timer',
                onClick: handleResetKycTimer,
                disabled: isResetTimerDisabled,
                confirmTitle: 'Reset verification timer',
                confirmContent: resetTimerConfirmContent,
              };

    const button = (
      <Button
        variant="outlined"
        className={classNames(
          classes.button,
          isPrimary && classes.primaryButton,
          actionKey === 'drop' && classes.destructiveButton
        )}
        onClick={actionConfig.onClick}
        disabled={actionConfig.disabled}>
        {actionConfig.label}
      </Button>
    );

    if (actionConfig.confirmTitle) {
      return (
        <Confirmation title={actionConfig.confirmTitle} content={actionConfig.confirmContent}>
          {button}
        </Confirmation>
      );
    }

    return button;
  };

  return (
    <Card className={classes.card}>
      <Box>
        <Box className={classes.header}>
          <Box>
            <Typography className={classes.sectionTitle}>Identity verification</Typography>
            <Typography className={classes.statusDescription}>{statusInfo.description}</Typography>
          </Box>
          <Box className={statusChipClass}>{statusInfo.label}</Box>
        </Box>
        {kycLoading && (
          <Box className={classes.loadingRow}>
            <CircularProgress size={16} />
            <Typography className={classes.helperText}>Updating...</Typography>
          </Box>
        )}
      </Box>

      <Box className={classes.header}>
        <Box className={classes.stripeLabel}>
          <Typography className={classes.label}>Stripe verification</Typography>
          <img className={classes.stripeIcon} src={stripeVerificationIcon} alt="" />
        </Box>
        <Button
          className={classes.button}
          variant="outlined"
          href={kycStripeLink || undefined}
          target="_blank"
          rel="noopener noreferrer"
          disabled={!kycStripeLink}>
          View in Stripe
          <img className={classes.externalLinkIcon} src={stripeExternalLinkIcon} alt="" />
        </Button>
      </Box>

      <Divider />

      <UserWriteAccess>
        <Box className={classes.actionsSection}>
          <Box className={classes.actionsSectionItem}>
            <Box className={classes.actionDescription}>
              <Typography className={classes.label}>Primary action</Typography>
              <Typography className={classes.helperText}>{primaryActionHelper}</Typography>
            </Box>
            {primaryActionKey && (
              <Box className={classes.actionGroup}>{renderActionButton(primaryActionKey)}</Box>
            )}
          </Box>
          {primaryActionKey === 'start' && (
            <Box className={classes.actionsSectionItem}>
              <Box className={classes.actionDescription}>
                <Typography className={classes.label}>Grace period</Typography>
                <Typography className={classes.helperText}>
                  How long user has to complete verification.
                </Typography>
              </Box>
              <Box className={classes.actionGroup}>{renderGracePeriodSelector()}</Box>
            </Box>
          )}
          <Box className={classes.actionsSectionItem}>
            <Box className={classes.actionDescription}>
              <Typography className={classes.label}>Other action</Typography>
              <Typography className={classes.helperText}>
                Actions are enabled when applicable to the current status or timer.
              </Typography>
            </Box>
            <Box className={classes.actionGroup}>
              {primaryActionKey !== 'start' && renderActionButton('start')}
              {primaryActionKey !== 'drop' && renderActionButton('drop')}
            </Box>
          </Box>
          {isResetTimerAvailable && (
            <Box className={classes.actionsSectionItem}>
              <Box className={classes.actionDescription}>
                <Typography className={classes.label}>Grace period</Typography>
                <Typography className={classes.helperText}>
                  How long user has to complete verification.
                </Typography>
              </Box>
              <Box className={classes.actionGroup}>
                {renderGracePeriodSelector()}
                {renderActionButton('reset')}
              </Box>
            </Box>
          )}
        </Box>

        <Divider />
      </UserWriteAccess>
      <Box className={classes.detailsRow}>
        <Typography className={classNames(classes.label, classes.detailsLabel)}>KYC ID</Typography>
        <Box className={classNames(classes.detailsValue, classes.sessionValue)}>
          <Typography className={classes.valueText}>{kycSessionId || 'N/A'}</Typography>
          {kycSessionId && (
            <IconButton
              className={classes.copyButton}
              aria-label="Copy KYC ID"
              onClick={handleCopyKycId}>
              <FileCopyOutlined />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box className={classes.detailsRow}>
        <Typography className={classNames(classes.label, classes.detailsLabel)}>
          KYC type
        </Typography>
        <Box className={classes.detailsValue}>
          <Typography className={classes.valueText}>{kycTypeInfo.label}</Typography>
          <Typography className={classes.helperText}>{kycTypeInfo.helper}</Typography>
        </Box>
      </Box>

      <Box className={classes.detailsRow}>
        <Typography className={classNames(classes.label, classes.detailsLabel)}>
          Time left
        </Typography>
        <Box className={classes.detailsValue}>
          <Typography className={classes.valueText}>{timerInfo.label}</Typography>
          <Typography className={classes.helperText}>{timerInfo.helper}</Typography>
        </Box>
      </Box>
    </Card>
  );
};

export default IdentityVerification;
